import { useEffect, useState } from 'react';
import { photoQueue } from '../platform/photo-queue.js';
import { livePendingOps, pendingChangesFor, pendingIntakes, type PendingChange } from '../platform/read-cache.js';

/**
 * THE MERGE RULE, MADE VISIBLE — offline plan v1.3 §2.2 C, increment 4.
 *
 * *Any cache primed from server reads MERGES pending queue items.* This is the consumer:
 * a screen showing an item — live OR from cache — also shows what the queue is about to do
 * to it. Without this, someone checks a garment out with no signal, reopens the app, and
 * sees `available` next to a garment that is in their hand.
 *
 * IT NAMES THE INTENT RATHER THAN PREDICTING THE OUTCOME, which is the decision argued in
 * read-cache.ts: a pending check-in can land on `available` or `flagged` depending on open
 * blocking flags, and that is a server-side question. Saying "a check-in is waiting to
 * send" is true; saying `available` might not be.
 */

const LABEL: Record<PendingChange, string> = {
  'checking-out': 'A check-out is saved on this device and waiting to send.',
  'checking-in': 'A check-in is saved on this device and waiting to send.',
  'adding-photo': 'A photo is saved on this device and waiting to send.',
  'raising-flag': 'A condition flag is saved on this device and waiting to send.',
};

export function usePendingChanges(itemId: string): PendingChange[] {
  const [changes, setChanges] = useState<PendingChange[]>([]);

  useEffect(() => {
    const sync = (): void => {
      setChanges(pendingChangesFor(itemId, livePendingOps(photoQueue().ops())));
    };
    const unsubscribe = photoQueue().subscribe(sync);
    sync();
    return unsubscribe;
  }, [itemId]);

  return changes;
}

export function PendingChanges({ itemId }: { itemId: string }): JSX.Element | null {
  const changes = usePendingChanges(itemId);
  if (changes.length === 0) return null;

  return (
    <ul className="pending-changes" role="status" aria-live="polite">
      {/* Keyed by index deliberately: two pending photos on one item are genuinely
          indistinguishable to a reader, and there is no stabler identity to offer. */}
      {changes.map((change, index) => (
        <li key={`${change}-${String(index)}`}>{LABEL[change]}</li>
      ))}
    </ul>
  );
}

/**
 * THE MERGE RULE'S INSERT ARM, rendered — increment 3's remainder.
 *
 * Items captured on this device that the server has never heard of. Without this,
 * somebody catalogues six garments in a basement, reopens the app, and the list shows
 * none of them — which reads as "my work is gone".
 *
 * THEY ARE NOT RENDERED AS ORDINARY ROWS, and cannot be: a pending intake has no id, no
 * status and no photo key, so putting it in the list proper would offer something that
 * cannot be opened. It is a separate, clearly-labelled group above the catalogue —
 * present, honest about what it is, and impossible to mistake for a saved item.
 */
export function PendingIntakeList(): JSX.Element | null {
  const [, forceRender] = useState(0);

  useEffect(
    () =>
      photoQueue().subscribe(() => {
        forceRender((n) => n + 1);
      }),
    [],
  );

  const intakes = pendingIntakes(livePendingOps(photoQueue().ops()));
  if (intakes.length === 0) return null;

  return (
    <section className="pending-intakes" aria-label="Captured on this device">
      <h3>Captured on this device, not sent yet</h3>
      <ul>
        {intakes.map((intake) => (
          <li key={intake.opId}>
            {intake.name}
            {intake.photoCount > 0 &&
              ` · ${String(intake.photoCount)} photo${intake.photoCount === 1 ? '' : 's'}`}
          </li>
        ))}
      </ul>
    </section>
  );
}
